/**
 * VirtualServerDetail (Phase 2.A.3).
 *
 * Drawer for one virtual server (curated bundle). Two modes:
 *   - mode='detail' + id   → load + edit existing
 *   - mode='create'        → blank form
 *
 * The composition picker is the substantial piece. Design choice for
 * V1 (15 to ~200 tools): a flat searchable list of all active tools
 * across the catalog, each row with a per-row exposure dropdown
 * (included / requestable / excluded / —not-in-bundle—). Search is
 * client-side (we already paged the parent list at the catalog
 * level). Bulk action: header "Set all visible to <exposure>".
 *
 * The 3-pane drag picker is V2 work; this fits 1000s of tools fine
 * with simple search + virtualization-when-needed.
 */
import { useEffect, useMemo, useState, type JSX } from 'react';
import { AlertTriangle, Check, Edit2, Globe2, Trash2, X } from 'lucide-react';
import type { McpTool } from '@sumcp/shared-types';

import {
  ApiError,
  useCatalogServers,
  useCatalogTools,
  useClients,
  useCreateVirtualServer,
  useDeleteVirtualServer,
  useUpdateVirtualServer,
  useVirtualServer,
  type CompositionEntry,
} from '@/api/index.js';
import { Drawer } from '../Drawer.js';
import { HelpCallout } from '../HelpCallout.js';
import { Required, RequiredLegend } from '../Required.js';

interface VirtualServerDetailProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly mode: 'detail' | 'create';
  readonly id?: string;
  readonly onPersisted?: (id: string) => void;
}

type Exposure = CompositionEntry['exposure'];

const STATUSES = ['draft', 'active', 'retired'] as const;

export function VirtualServerDetail({
  open,
  onClose,
  mode,
  id,
  onPersisted,
}: VirtualServerDetailProps): JSX.Element {
  const vs = useVirtualServer(mode === 'detail' ? id : undefined);
  const tools = useCatalogTools();
  const servers = useCatalogServers();
  const clients = useClients({ limit: 500 });

  const create = useCreateVirtualServer();
  const update = useUpdateVirtualServer();
  const remove = useDeleteVirtualServer();

  const [editing, setEditing] = useState(mode === 'create');
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [endpointUrl, setEndpointUrl] = useState('');
  const [allowedClientIds, setAllowedClientIds] = useState<string[]>([]);
  const [composition, setComposition] = useState<CompositionEntry[]>([]);
  const [perUserRps, setPerUserRps] = useState<string>('');
  const [perToolRps, setPerToolRps] = useState<string>('');
  const [status, setStatus] = useState<(typeof STATUSES)[number]>('draft');
  const [actionError, setActionError] = useState<string | null>(null);

  // Composition picker UI state.
  const [toolSearch, setToolSearch] = useState('');
  const [exposureFilter, setExposureFilter] = useState<'all' | Exposure | 'unset'>('all');

  useEffect(() => {
    if (mode === 'detail' && vs.data) {
      setSlug(vs.data.slug);
      setName(vs.data.name);
      setDesc((vs.data.description as string | null) ?? '');
      setEndpointUrl(vs.data.endpointUrl);
      setAllowedClientIds([...vs.data.allowedClientIds]);
      const comp = (vs.data.composition as CompositionEntry[] | null) ?? [];
      setComposition(comp);
      const r = (vs.data.rateLimitOverrides ?? {}) as {
        per_user_rps?: number;
        per_tool_rps?: number;
      };
      setPerUserRps(r.per_user_rps != null ? String(r.per_user_rps) : '');
      setPerToolRps(r.per_tool_rps != null ? String(r.per_tool_rps) : '');
      setStatus((vs.data.status as 'draft' | 'active' | 'retired') ?? 'draft');
      setEditing(false);
    } else if (mode === 'create' && open) {
      setSlug('');
      setName('');
      setDesc('');
      setEndpointUrl(`${window.location.origin}/v//mcp`);
      setAllowedClientIds([]);
      setComposition([]);
      setPerUserRps('');
      setPerToolRps('');
      setStatus('draft');
      setToolSearch('');
      setExposureFilter('all');
      setEditing(true);
    }
  }, [mode, vs.data, open]);

  // Auto-fill the canonical endpoint URL when slug changes during create.
  useEffect(() => {
    if (mode === 'create' && slug) {
      setEndpointUrl(`${window.location.origin}/v/${slug}/mcp`);
    }
  }, [slug, mode]);

  const compMap = useMemo(() => {
    const m = new Map<string, Exposure>();
    for (const c of composition) m.set(c.tool_id, c.exposure);
    return m;
  }, [composition]);

  const allTools = useMemo(() => {
    const list: McpTool[] = [
      ...(tools.data?.available ?? []),
      ...(tools.data?.requestable ?? []).map((r) => r.tool),
    ];
    // De-dupe by id
    const seen = new Set<string>();
    return list.filter((t) => {
      if (seen.has(t.id)) return false;
      seen.add(t.id);
      return true;
    });
  }, [tools.data]);

  const serverNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of servers.data?.items ?? []) m.set(s.id, s.name);
    return m;
  }, [servers.data]);

  const filteredTools = useMemo(() => {
    const q = toolSearch.trim().toLowerCase();
    return allTools.filter((t) => {
      if (
        q &&
        !(
          t.name.toLowerCase().includes(q) ||
          (t.display_name ?? '').toLowerCase().includes(q) ||
          (t.description ?? '').toLowerCase().includes(q)
        )
      ) {
        return false;
      }
      const exp = compMap.get(t.id);
      if (exposureFilter === 'all') return true;
      if (exposureFilter === 'unset') return exp == null;
      return exp === exposureFilter;
    });
  }, [allTools, compMap, exposureFilter, toolSearch]);

  function setToolExposure(toolId: string, exposure: Exposure | null): void {
    setComposition((prev) => {
      const without = prev.filter((c) => c.tool_id !== toolId);
      if (exposure == null) return without;
      return [...without, { tool_id: toolId, exposure, policy: 'inherited' }];
    });
  }

  function bulkSetVisible(exposure: Exposure | null): void {
    const targetIds = new Set(filteredTools.map((t) => t.id));
    setComposition((prev) => {
      const others = prev.filter((c) => !targetIds.has(c.tool_id));
      if (exposure == null) return others;
      const additions = filteredTools.map((t) => ({
        tool_id: t.id,
        exposure,
        policy: 'inherited' as const,
      }));
      return [...others, ...additions];
    });
  }

  function toggleClient(clientId: string): void {
    setAllowedClientIds((prev) =>
      prev.includes(clientId) ? prev.filter((c) => c !== clientId) : [...prev, clientId],
    );
  }

  function handleResult<T>(p: Promise<T>, after?: (v: T) => void): void {
    setActionError(null);
    p.then((v) => after?.(v)).catch((err: unknown) => {
      setActionError(
        err instanceof ApiError
          ? `${err.status}: ${JSON.stringify(err.body)}`
          : (err as Error).message,
      );
    });
  }

  function buildRateLimitPayload(): { per_user_rps?: number; per_tool_rps?: number } | undefined {
    const pl: { per_user_rps?: number; per_tool_rps?: number } = {};
    if (perUserRps && !Number.isNaN(Number(perUserRps))) pl.per_user_rps = Number(perUserRps);
    if (perToolRps && !Number.isNaN(Number(perToolRps))) pl.per_tool_rps = Number(perToolRps);
    return Object.keys(pl).length === 0 ? undefined : pl;
  }

  function onSave(): void {
    setActionError(null);
    if (!name || !slug || !endpointUrl) {
      setActionError('slug + name + endpoint_url are required');
      return;
    }
    if (mode === 'create') {
      handleResult(
        create.mutateAsync({
          slug,
          name,
          description: desc || undefined,
          endpoint_url: endpointUrl,
          allowed_client_ids: allowedClientIds,
          composition,
          rate_limit_overrides: buildRateLimitPayload(),
          status,
        }),
        (v) => {
          onPersisted?.(v.id);
          onClose();
        },
      );
      return;
    }
    if (!vs.data) return;
    handleResult(
      update.mutateAsync({
        id: vs.data.id,
        patch: {
          name,
          description: desc || undefined,
          endpoint_url: endpointUrl,
          allowed_client_ids: allowedClientIds,
          composition,
          rate_limit_overrides: buildRateLimitPayload() ?? {},
          status,
        },
      }),
      (v) => {
        setEditing(false);
        onPersisted?.(v.id);
      },
    );
  }

  function onDelete(): void {
    if (!vs.data) return;
    if (!window.confirm(`Delete virtual server "${vs.data.name}"? This is permanent.`)) return;
    handleResult(remove.mutateAsync(vs.data.id), () => onClose());
  }

  const titleEl = mode === 'create' ? 'New virtual server' : (vs.data?.name ?? id ?? '...');
  const persisting = create.isPending || update.isPending || remove.isPending;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow="Virtual server"
      title={titleEl}
      footer={
        editing ? (
          <>
            <button
              type="button"
              onClick={() => (mode === 'create' ? onClose() : setEditing(false))}
            >
              <X size={14} aria-hidden /> Cancel
            </button>
            <button
              type="button"
              className="primary"
              onClick={onSave}
              disabled={persisting || !name || !slug}
            >
              <Check size={14} aria-hidden /> {mode === 'create' ? 'Create' : 'Save'}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="danger" onClick={onDelete} disabled={persisting}>
              <Trash2 size={14} aria-hidden /> Delete
            </button>
            <button type="button" onClick={() => setEditing(true)}>
              <Edit2 size={14} aria-hidden /> Edit
            </button>
          </>
        )
      }
    >
      <div className="drawer-stack">
        {mode === 'detail' && vs.isLoading && <p className="subtle">Loading…</p>}

        <HelpCallout
          title={mode === 'create' ? 'Creating a virtual server' : 'About this virtual server'}
          whatHappens={
            <>
              A virtual server is a curated bundle of tools served at{' '}
              <code>/v/&lt;slug&gt;/mcp</code>. It does not host backends — it composes existing
              tools from one or more registered MCP servers into one endpoint, with per-tool
              exposure (<code>included</code> / <code>requestable</code> / <code>excluded</code>).
              Saving emits a <code>virtual_server.create</code> or <code>.update</code> audit event.
            </>
          }
          youNeed={[
            <>A unique slug (lowercase, dashes) — the slug becomes the public path.</>,
            <>The set of tools you want in the bundle (added in the Composition panel below).</>,
            <>
              The OAuth clients you want to allow (added in Allowed clients). Empty = any client
              with the right WG entitlements can reach it.
            </>,
          ]}
          afterwards={[
            <>
              Point a consumer at <code>/v/&lt;slug&gt;/mcp</code> instead of the global{' '}
              <code>/mcp</code> — they'll only see this curated tool list.
            </>,
            <>
              Per-tool exposure is enforced at runtime; <code>excluded</code> tools never appear
              even if the user is otherwise entitled.
            </>,
          ]}
        />

        <fieldset style={fieldsetStyle} disabled={!editing}>
          <legend style={legendStyle}>Identity</legend>
          {editing && <RequiredLegend />}
          <Field label="Slug" required={mode === 'create'}>
            {mode === 'create' ? (
              <input
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="research-data"
                style={inputStyle}
                required
              />
            ) : (
              <code>{slug}</code>
            )}
          </Field>
          <Field label="Display name" required>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={inputStyle}
              required
            />
          </Field>
          <Field label="Description">
            <textarea
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              rows={2}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </Field>
          <Field label="Endpoint URL" required>
            <input
              value={endpointUrl}
              onChange={(e) => setEndpointUrl(e.target.value)}
              style={inputStyle}
              required
            />
          </Field>
          <Field label="Status">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as (typeof STATUSES)[number])}
              style={inputStyle}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
        </fieldset>

        {/* Allowed clients */}
        <fieldset style={fieldsetStyle} disabled={!editing}>
          <legend style={legendStyle}>Allowed clients ({allowedClientIds.length})</legend>
          <p className="subtle" style={{ fontSize: 13, margin: '0 0 8px' }}>
            OAuth clients that may use this bundle's URL. Empty = no client allowed (deny by
            default).
          </p>
          <div
            style={{
              maxHeight: 160,
              overflow: 'auto',
              border: '1px solid var(--line)',
              borderRadius: 8,
            }}
          >
            <table className="data-table" style={{ fontSize: 13 }}>
              <tbody>
                {(clients.data?.items ?? []).map((c) => (
                  <tr key={c.id}>
                    <td style={{ padding: '4px 8px' }}>
                      <label
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={allowedClientIds.includes(c.id)}
                          onChange={() => toggleClient(c.id)}
                          disabled={!editing}
                        />
                        <strong>{c.displayName}</strong> <span className="subtle">({c.type})</span>
                      </label>
                    </td>
                  </tr>
                ))}
                {clients.data?.items.length === 0 && (
                  <tr>
                    <td className="subtle" style={{ padding: 8 }}>
                      No OAuth clients registered yet — create one in the OAuth Clients tab first.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </fieldset>

        {/* Rate limits */}
        <fieldset style={fieldsetStyle} disabled={!editing}>
          <legend style={legendStyle}>Rate-limit overrides (optional)</legend>
          <Field label="per-user RPS">
            <input
              value={perUserRps}
              onChange={(e) => setPerUserRps(e.target.value.replace(/[^0-9.]/g, ''))}
              placeholder="(inherit)"
              style={inputStyle}
            />
          </Field>
          <Field label="per-tool RPS">
            <input
              value={perToolRps}
              onChange={(e) => setPerToolRps(e.target.value.replace(/[^0-9.]/g, ''))}
              placeholder="(inherit)"
              style={inputStyle}
            />
          </Field>
        </fieldset>

        {/* Composition picker */}
        <fieldset style={fieldsetStyle} disabled={!editing}>
          <legend style={legendStyle}>
            Composition ({composition.filter((c) => c.exposure === 'included').length} included
            {' / '}
            {composition.filter((c) => c.exposure === 'requestable').length} requestable
            {' / '}
            {composition.filter((c) => c.exposure === 'excluded').length} excluded)
          </legend>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            <input
              placeholder="Search tool name / description…"
              value={toolSearch}
              onChange={(e) => setToolSearch(e.target.value)}
              style={{ ...inputStyle, flex: '1 1 200px' }}
            />
            <select
              value={exposureFilter}
              onChange={(e) => setExposureFilter(e.target.value as typeof exposureFilter)}
              style={inputStyle}
            >
              <option value="all">All ({allTools.length})</option>
              <option value="included">Included only</option>
              <option value="requestable">Requestable only</option>
              <option value="excluded">Excluded only</option>
              <option value="unset">Not in bundle</option>
            </select>
          </div>

          {editing && filteredTools.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              <span className="subtle" style={{ fontSize: 13 }}>
                Bulk-apply to {filteredTools.length} visible:
              </span>
              <button type="button" onClick={() => bulkSetVisible('included')} style={bulkBtnStyle}>
                included
              </button>
              <button
                type="button"
                onClick={() => bulkSetVisible('requestable')}
                style={bulkBtnStyle}
              >
                requestable
              </button>
              <button type="button" onClick={() => bulkSetVisible('excluded')} style={bulkBtnStyle}>
                excluded
              </button>
              <button type="button" onClick={() => bulkSetVisible(null)} style={bulkBtnStyle}>
                remove
              </button>
            </div>
          )}

          <div
            style={{
              maxHeight: 320,
              overflow: 'auto',
              border: '1px solid var(--line)',
              borderRadius: 8,
            }}
          >
            <table className="data-table" style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th>Tool</th>
                  <th style={{ width: 140 }}>Server</th>
                  <th style={{ width: 110 }}>Class</th>
                  <th style={{ width: 130 }}>Exposure</th>
                </tr>
              </thead>
              <tbody>
                {filteredTools.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: 12 }}>
                      <span className="subtle">
                        {tools.isLoading ? 'Loading tools…' : 'No tools match the filter.'}
                      </span>
                    </td>
                  </tr>
                )}
                {filteredTools.map((t) => {
                  const exp = compMap.get(t.id);
                  return (
                    <tr key={t.id}>
                      <td>
                        <div>
                          <code>{t.name}</code>
                        </div>
                        {t.display_name && t.display_name !== t.name && (
                          <div className="subtle" style={{ fontSize: 12 }}>
                            {t.display_name}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className="subtle" style={{ fontSize: 12 }}>
                          {serverNameById.get(t.server_id) ?? t.server_id.slice(0, 8) + '…'}
                        </span>
                      </td>
                      <td>
                        <span className="subtle" style={{ fontSize: 12 }}>
                          {t.data_classification}
                        </span>
                      </td>
                      <td>
                        <select
                          value={exp ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setToolExposure(t.id, v === '' ? null : (v as Exposure));
                          }}
                          disabled={!editing}
                          style={{
                            ...inputStyle,
                            padding: '4px 6px',
                            fontSize: 12,
                            background:
                              exp === 'included'
                                ? 'rgba(95,178,122,0.15)'
                                : exp === 'requestable'
                                  ? 'rgba(224,168,92,0.15)'
                                  : exp === 'excluded'
                                    ? 'rgba(217,119,102,0.15)'
                                    : 'transparent',
                          }}
                        >
                          <option value="">— not in bundle —</option>
                          <option value="included">included</option>
                          <option value="requestable">requestable</option>
                          <option value="excluded">excluded</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="subtle" style={{ fontSize: 12, marginTop: 8 }}>
            <Globe2 size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Only <strong>included</strong> tools surface in <code>tools/list</code> at this
            endpoint. <strong>requestable</strong> shows up in My Access as "request to add to
            bundle". <strong>excluded</strong> denies even when the user's WG would normally allow
            it.
          </p>
        </fieldset>

        {actionError && (
          <p style={{ color: '#c0392b', fontSize: 14 }} role="alert">
            <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {actionError}
          </p>
        )}
      </div>
    </Drawer>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(140px, max-content) 1fr',
        gap: 12,
        alignItems: 'center',
        marginBottom: 8,
      }}
    >
      <span className="subtle" style={{ fontSize: 13 }}>
        {label}
        {required && <Required />}
      </span>
      <span style={{ fontSize: 14 }}>{children}</span>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  border: '1px solid var(--line)',
  borderRadius: 8,
  fontSize: 14,
  background: 'transparent',
  color: 'inherit',
  width: '100%',
  boxSizing: 'border-box',
};

const fieldsetStyle: React.CSSProperties = {
  border: '1px solid var(--line)',
  borderRadius: 8,
  padding: 12,
};

const legendStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  padding: '0 6px',
  color: 'var(--muted-foreground, #888)',
};

const bulkBtnStyle: React.CSSProperties = {
  padding: '2px 8px',
  borderRadius: 6,
  border: '1px solid var(--line)',
  background: 'rgba(255,255,255,0.5)',
  fontSize: 12,
  cursor: 'pointer',
};
